import { useCallback, useMemo } from 'react';
import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useQueries,
  useQuery,
  useSuspenseQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { CustomersDeleteParams } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { listLogsExactly } from '../activity-log/lossless';
import { isEmptyString } from '@/utils/string';
import type { QueryConfig } from '../shared/react-query-types';
import { parseObject } from '../object/entity';
import { sortObjectTypesByName, type ObjectType } from '../object-type/entity';
import { requireWorkspaceId } from '../shared/workspace';
import { parseProfileShortcuts } from '../profile-shortcut/entity';
import {
  customer,
  customerKeys,
  type CustomerLogsParams,
  type PeopleListParams,
  type RelatedObjectsPageParams,
} from './entity';

const STALE_TIME = 60_000;

/* ------------------------------------------------------------------------- */
/* Test users                                                                 */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getTestUsers(workspaceId: number, verified?: boolean) {
  return hydraUiApi.customers.getTestUsers({
    params: {
      path: { environment_id: String(workspaceId) },
      query: verified == null ? undefined : { verified },
    },
  });
}

/** @public */
export const getTestUsersQueryOptions = (workspaceId: number, verified?: boolean) =>
  queryOptions({
    queryKey: customerKeys.testUsers(workspaceId, verified),
    queryFn: () => getTestUsers(workspaceId, verified),
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });

interface UseTestUsersOptionalOptions {
  workspaceId: number;
  verified?: boolean;
  enabled?: boolean;
  refetchInterval?: number | false | (() => number | false);
}

/** @public */
export const useTestUsersOptional = ({
  workspaceId,
  verified,
  enabled = true,
  refetchInterval,
}: UseTestUsersOptionalOptions) => {
  const active = enabled && workspaceId > 0;
  return useQuery({
    queryKey: active ? customerKeys.testUsers(workspaceId, verified) : ['customer', 'test-users', 'inactive'],
    queryFn: active ? () => getTestUsers(workspaceId, verified) : skipToken,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
    refetchInterval,
  });
};

/* ------------------------------------------------------------------------- */
/* Test-user count                                                            */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getTestUserCount(workspaceId: number) {
  return hydraUiApi.customers.getTestUsersCount({ params: { path: { environment_id: String(workspaceId) } } });
}

interface UseTestUserCountOptionalOptions {
  workspaceId: number;
  enabled?: boolean;
}

/** @public */
export const useTestUserCountOptional = ({ workspaceId, enabled = true }: UseTestUserCountOptionalOptions) => {
  const active = enabled && workspaceId > 0;
  return useQuery({
    queryKey: active ? customerKeys.testUserCount(workspaceId) : ['customer', 'test-user-count', 'inactive'],
    queryFn: active ? () => getTestUserCount(workspaceId) : skipToken,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
};

/* ------------------------------------------------------------------------- */
/* People list                                                                */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getPeople(workspaceId: number, params: PeopleListParams) {
  return hydraUiApi.customers.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: {
        asc: params.asc,
        page: params.page,
        sort: params.sort ?? 'created_at',
        ...(params.filters == null ? {} : { filters: params.filters }),
        ...(params.email == null ? {} : { email: params.email }),
        active: params.active === true ? true : undefined,
      },
    },
  });
}

/**
 * Options factory for the people list, for call sites that need cache
 * overrides — the list page reads it with `staleTime: 0` so returning from an
 * Ember-owned add/merge flow never shows a stale page.
 *
 * @public
 */
export const getPeopleQueryOptions = (workspaceId: number, params: PeopleListParams) =>
  queryOptions({
    queryKey: customerKeys.people(workspaceId, params),
    queryFn: () => getPeople(workspaceId, params),
    staleTime: STALE_TIME,
  });

interface UsePeopleOptionalOptions {
  workspaceId: number;
  params: PeopleListParams;
  enabled?: boolean;
}

/** @public */
export const usePeopleOptional = ({ workspaceId, params, enabled = true }: UsePeopleOptionalOptions) => {
  const active = enabled && workspaceId > 0;
  return useQuery({
    queryKey: active ? customerKeys.people(workspaceId, params) : ['customer', 'people', 'inactive'],
    queryFn: active ? () => getPeople(workspaceId, params) : skipToken,
    staleTime: STALE_TIME,
  });
};

/* ------------------------------------------------------------------------- */
/* Filtered people count                                                      */
/* ------------------------------------------------------------------------- */

/** @public Count people matching a base64-encoded condition filter. */
export async function getFilteredPeopleCount(workspaceId: number, filters: string) {
  const { pagination } = await getPeopleMatching(workspaceId, { filters });
  return pagination.total;
}

interface UseFilteredPeopleOptions {
  workspaceId: number;
  filters: string;
}

/**
 * Non-suspending read of the first page of people matching a base64-encoded
 * condition filter, through `getPeopleMatching`. A caller that needs both the
 * people and their count reads both off this one request, rather than asking
 * again through `useFilteredPeopleCount`.
 *
 * @public
 */
export const useFilteredPeople = ({ workspaceId, filters }: UseFilteredPeopleOptions) => {
  const active = workspaceId > 0;
  return useQuery({
    queryKey: active ? customerKeys.filteredPeople(workspaceId, filters) : ['customer', 'filtered-people', 'inactive'],
    queryFn: active ? () => getPeopleMatching(workspaceId, { filters }) : skipToken,
    staleTime: STALE_TIME,
  });
};

interface UseFilteredPeopleCountOptions {
  workspaceId: number;
  filters: string;
  enabled?: boolean;
}

/** @public */
export const useFilteredPeopleCount = ({ workspaceId, filters, enabled = true }: UseFilteredPeopleCountOptions) => {
  const active = enabled && workspaceId > 0;
  return useQuery({
    queryKey: active ? customerKeys.filteredCount(workspaceId, filters) : ['customer', 'filtered-count', 'inactive'],
    queryFn: active ? () => getFilteredPeopleCount(workspaceId, filters) : skipToken,
    // The disable-email migration gates on this count, so it must reflect the workspace as
    // it is each time the modal opens — not a value cached from a previous open. Ember re-ran
    // the check on every open; drop the cache so each open is a fresh, loading-gated fetch
    // rather than a stale zero that could wave through profiles added since the last check.
    staleTime: 0,
    gcTime: 0,
  });
};

/* ------------------------------------------------------------------------- */
/* Audience match                                                             */
/* ------------------------------------------------------------------------- */

/** How many people an audience reaches, and the people index's notice when it is degraded. */
export type AudienceMatch = { total: number; message: string };

/**
 * How many people match a base64-encoded condition filter, or everyone
 * without one, with the notice the endpoint sends when its index is
 * degraded (`meta.message`).
 */
async function getAudienceMatch(workspaceId: number, filters: string | undefined): Promise<AudienceMatch> {
  const response = await hydraUiApi.customers.list({
    params: { path: { environment_id: String(workspaceId) }, query: filters == null ? {} : { filters } },
  });
  return { total: response.meta.pagination.total, message: response.meta.message ?? '' };
}

interface UseAudienceMatchOptions {
  workspaceId: number;
  filters: string | undefined;
  enabled?: boolean;
}

/**
 * An audience's match count, read fresh each time the audience changes, as
 * Ember's recipients step queries it on every change.
 *
 * @public
 */
export const useAudienceMatch = ({ workspaceId, filters, enabled = true }: UseAudienceMatchOptions) => {
  const active = enabled && workspaceId > 0;
  return useQuery({
    queryKey: customerKeys.audienceMatch(workspaceId, filters),
    queryFn: active ? () => getAudienceMatch(workspaceId, filters) : skipToken,
    staleTime: 0,
  });
};

/* ------------------------------------------------------------------------- */
/* Welcome / empty-state probe                                                */
/* ------------------------------------------------------------------------- */

/**
 * Unfiltered total profile count. When the filtered list is empty, the page
 * uses this to decide between the "no results" and the first-run "welcome"
 * empty states — mirroring the Ember route's unfiltered `store.query('customer', {})`
 * probe.
 *
 * @public
 */
export async function getUnfilteredPeopleCount(workspaceId: number) {
  const response = await hydraUiApi.customers.list({
    params: { path: { environment_id: String(workspaceId) }, query: {} },
  });
  return response.meta.pagination.total;
}

interface UseUnfilteredPeopleCountOptionalOptions {
  workspaceId: number;
  enabled?: boolean;
}

/** @public */
export const useUnfilteredPeopleCountOptional = ({
  workspaceId,
  enabled = true,
}: UseUnfilteredPeopleCountOptionalOptions) => {
  const active = enabled && workspaceId > 0;
  return useQuery({
    queryKey: active ? customerKeys.unfilteredCount(workspaceId) : ['customer', 'unfiltered-count', 'inactive'],
    queryFn: active ? () => getUnfilteredPeopleCount(workspaceId) : skipToken,
    staleTime: STALE_TIME,
  });
};

/* ------------------------------------------------------------------------- */
/* Email lookup                                                               */
/* ------------------------------------------------------------------------- */

/**
 * The first page of profiles an email lookup or a condition filter matches,
 * in the endpoint's own order, with the page it read. Only the lookup is
 * sent, so a query with neither reads the first page of everyone. The page
 * lets a caller tell an empty result from one the viewer may not see: a
 * filter on a sensitive attribute counts matches it then leaves out.
 *
 * @public
 */
export async function getPeopleMatching(workspaceId: number, query: Pick<PeopleListParams, 'email' | 'filters'>) {
  const response = await hydraUiApi.customers.list({
    params: { path: { environment_id: String(workspaceId) }, query },
  });
  return {
    customers: response.customers.map((raw) => customer.parse(raw)),
    pagination: response.meta.pagination,
  };
}

/* ------------------------------------------------------------------------- */
/* Customer detail record                                                     */
/* ------------------------------------------------------------------------- */

/**
 * A single profile by id. The `customerId` may be the external id or the
 * internal `cio_id` — the backend resolves either. Devices and attributes come
 * back embedded in this payload (there is no separate devices endpoint), so a
 * profile's device list is `customer.devices`.
 *
 * @public
 */
export async function getCustomer(workspaceId: number, customerId: string) {
  const data = await hydraUiApi.customers.get({
    params: { path: { environment_id: String(workspaceId), customer_id: customerId } },
  });
  return customer.parse(data.customer);
}

/** @public */
export const getCustomerQueryOptions = (workspaceId: number, customerId: string) =>
  queryOptions({
    queryKey: customerKeys.detail(workspaceId, customerId),
    queryFn: () => getCustomer(workspaceId, customerId),
    staleTime: STALE_TIME,
  });

/** @public */
export const useCustomer = ({
  customerId,
  queryConfig,
}: {
  customerId: string;
  queryConfig?: QueryConfig<typeof getCustomerQueryOptions>;
}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getCustomerQueryOptions(workspaceId, customerId), ...queryConfig });
};

/* ------------------------------------------------------------------------- */
/* Opt-outs (per-channel unsubscribe state)                                   */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getCustomerOptOuts(workspaceId: number, customerId: string) {
  const data = await hydraUiApi.customers.getOptouts({
    params: { path: { environment_id: String(workspaceId), customer_id: customerId } },
  });
  return data.opt_outs;
}

/** @public */
export const getCustomerOptOutsQueryOptions = (workspaceId: number, customerId: string) =>
  queryOptions({
    queryKey: customerKeys.optOuts(workspaceId, customerId),
    queryFn: () => getCustomerOptOuts(workspaceId, customerId),
    // Read afresh on each visit, as Ember does: a removal lands a moment after it is queued.
    staleTime: 0,
  });

/** @public */
export const useCustomerOptOuts = ({
  customerId,
  queryConfig,
}: {
  customerId: string;
  queryConfig?: QueryConfig<typeof getCustomerOptOutsQueryOptions>;
}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getCustomerOptOutsQueryOptions(workspaceId, customerId), ...queryConfig });
};

/* ------------------------------------------------------------------------- */
/* Consent state                                                              */
/* ------------------------------------------------------------------------- */

/** @public A profile's standing consent records and how far the import of earlier consent has reached it. */
export async function getCustomerConsentState(workspaceId: number, cioId: string) {
  return hydraUiApi.customers.getConsent({
    params: { path: { environment_id: String(workspaceId), customer_id: cioId } },
  });
}

/** @public */
export const getCustomerConsentStateQueryOptions = (workspaceId: number, cioId: string) =>
  queryOptions({
    queryKey: customerKeys.consentState(workspaceId, cioId),
    queryFn: () => getCustomerConsentState(workspaceId, cioId),
    // Read afresh on each visit, as Ember does: consent changes outside Hydra too.
    staleTime: 0,
  });

/* ------------------------------------------------------------------------- */
/* Profile shortcuts (rendered)                                               */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getCustomerProfileShortcuts(workspaceId: number, customerId: string) {
  const data = await hydraUiApi.shortcuts.getCustomersProfileShortcuts({
    params: { path: { environment_id: String(workspaceId), customer_id: customerId } },
  });
  return parseProfileShortcuts(data.profile_shortcuts);
}

/** @public */
export const getCustomerProfileShortcutsQueryOptions = (workspaceId: number, customerId: string) =>
  queryOptions({
    queryKey: customerKeys.profileShortcuts(workspaceId, customerId),
    queryFn: () => getCustomerProfileShortcuts(workspaceId, customerId),
    staleTime: STALE_TIME,
  });

/** @public */
export const useCustomerProfileShortcuts = ({
  customerId,
  queryConfig,
}: {
  customerId: string;
  queryConfig?: QueryConfig<typeof getCustomerProfileShortcutsQueryOptions>;
}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getCustomerProfileShortcutsQueryOptions(workspaceId, customerId), ...queryConfig });
};

/* ------------------------------------------------------------------------- */
/* Related objects (relationships fan-out, keyed by the profile's cio id)     */
/* ------------------------------------------------------------------------- */

/** Preview of related objects across every type (≤10 per type). @public */
export async function getRelatedObjectsAllTypes(workspaceId: number, cioId: string) {
  return hydraUiApi.objects.getRelationshipsObjectsRelatedToAllTypes({
    params: { path: { environment_id: String(workspaceId), cio_id: cioId } },
  });
}

/**
 * A single object type's related objects for a profile, paginated — the per-type
 * tables on the Relationships tab (Ember's `relationships-table`, one per object
 * type). `params` carries `page`/`size`; the response `meta.pagination.total`
 * drives the pager (see `isUnknownPeopleTotal`-style callers).
 *
 * @public
 */
export async function getRelatedObjectsForType(
  workspaceId: number,
  cioId: string,
  typeId: string,
  params?: RelatedObjectsPageParams,
) {
  const data = await hydraUiApi.objects.getObjectTypesRelationshipsObjectsRelatedTo({
    params: {
      path: { environment_id: String(workspaceId), type_id: typeId, cio_id: cioId },
      query: params ?? {},
    },
  });
  return { ...data, objects: data.objects.map(parseObject) };
}

/**
 * The Relationships tab reads fresh on every entry, as Ember's tab does: a relationship can change
 * elsewhere (an object page, the API) without touching this cache.
 *
 * @public
 */
export const getRelatedObjectsForTypeQueryOptions = (
  workspaceId: number,
  cioId: string,
  typeId: string,
  params: RelatedObjectsPageParams = {},
) =>
  queryOptions({
    queryKey: customerKeys.relatedObjectsForType(workspaceId, cioId, typeId, params),
    queryFn: () => getRelatedObjectsForType(workspaceId, cioId, typeId, params),
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

/** One row is enough to tell whether a type has any relationships. */
const RELATED_TYPE_PROBE: RelatedObjectsPageParams = { page: 1, size: 1 };

/**
 * Whether a profile has any relationship of one type. It reads only the relationship rows and
 * leaves the objects unparsed, so one malformed object cannot fail the whole tab.
 */
async function hasRelatedObjectsOfType(workspaceId: number, cioId: string, typeId: string) {
  const data = await hydraUiApi.objects.getObjectTypesRelationshipsObjectsRelatedTo({
    params: {
      path: { environment_id: String(workspaceId), type_id: typeId, cio_id: cioId },
      query: RELATED_TYPE_PROBE,
    },
  });
  return data.relationships.length > 0;
}

/**
 * The object types a profile has relationships with, in name order. One single-row read per type,
 * as Ember's `relationships.fetchRelationshipsForObjectTypes` makes them; the Relationships tab
 * draws a table for each type this returns. Like the tables, it reads fresh on every entry.
 *
 * @public
 */
export function useRelatedObjectTypes(cioId: string, objectTypes: readonly ObjectType[]) {
  const workspaceId = requireWorkspaceId();
  const sorted = useMemo(() => sortObjectTypesByName(objectTypes), [objectTypes]);
  const combine = useCallback(
    (results: Array<UseQueryResult<boolean>>) => ({
      objectTypes: sorted.filter((_, index) => results[index]?.data === true),
      isPending: results.some((result) => result.isPending),
      isError: results.some((result) => result.isError),
    }),
    [sorted],
  );
  return useQueries({
    queries: sorted.map((type) =>
      queryOptions({
        queryKey: customerKeys.relatedObjectTypeProbe(workspaceId, cioId, type.id),
        queryFn: () => hasRelatedObjectsOfType(workspaceId, cioId, type.id),
        staleTime: 0,
      }),
    ),
    combine,
  });
}

export async function searchCustomersByEmailPrefix(workspaceId: number, term: string, signal?: AbortSignal) {
  const query: Pick<PeopleListParams, 'email'> = {};
  if (!isEmptyString(term)) query.email = `${term}*`;
  return hydraUiApi.customers.list({ params: { path: { environment_id: String(workspaceId) }, query }, signal });
}

/** How many of a filter-based bulk delete's matches were recently active. The body is the delete's own. */
export async function previewCustomersDelete(workspaceId: number, body: CustomersDeleteParams, signal: AbortSignal) {
  return hydraUiApi.customers.deletePreview({
    params: { path: { environment_id: String(workspaceId) } },
    body,
    signal,
  });
}

export const searchCustomersByEmailPrefixQueryOptions = (workspaceId: number, term: string) =>
  queryOptions({
    queryKey: [...customerKeys.all(workspaceId), 'email-prefix', term],
    queryFn: ({ signal }) => searchCustomersByEmailPrefix(workspaceId, term, signal),
    staleTime: 0,
    retry: false,
  });

/* ------------------------------------------------------------------------- */
/* Activity logs                                                              */
/* ------------------------------------------------------------------------- */

/** One page of a profile's own activity logs, read by its internal id (`cio_id`). */
export function getCustomerLogs(workspaceId: number, cioId: string, params: CustomerLogsParams = {}) {
  return listLogsExactly(workspaceId, { ...params, internal_id: cioId });
}

/** Always refetched on entry, so a change made on another tab or in the embedded app shows up here. */
export const getCustomerLogsQueryOptions = (workspaceId: number, cioId: string, params: CustomerLogsParams = {}) =>
  queryOptions({
    queryKey: customerKeys.logs(workspaceId, cioId, params),
    queryFn: () => getCustomerLogs(workspaceId, cioId, params),
    staleTime: 0,
  });
